/**
 * VH-83: TanStack Query hooks for the recipe library.
 *
 * Exposes thin wrappers around the recipesAPI service so pages and components
 * stay declarative. After mutations we invalidate the relevant list/detail
 * queries — recipe edits create new versions on the backend, so detail and
 * version-DAG caches must refetch.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { recipesAPI } from '../services/recipes'

// ---- Query key factories -------------------------------------------------

export const recipeKeys = {
  all: ['recipes'],
  lists: () => [...recipeKeys.all, 'list'],
  list: (params) => [...recipeKeys.lists(), params || {}],
  details: () => [...recipeKeys.all, 'detail'],
  detail: (id) => [...recipeKeys.details(), id],
  versions: (id) => [...recipeKeys.all, 'versions', id],
  categories: (lang) => [...recipeKeys.all, 'categories', lang || 'en'],
  equipment: ['profile', 'equipment'],
}

// ---- Read hooks ----------------------------------------------------------

export function useRecipes(params = {}, options = {}) {
  return useQuery({
    queryKey: recipeKeys.list(params),
    queryFn: () => recipesAPI.list(params).then((r) => r.data),
    staleTime: 1000 * 30,
    ...options,
  })
}

export function useRecipe(recipeId, options = {}) {
  return useQuery({
    queryKey: recipeKeys.detail(recipeId),
    queryFn: () => recipesAPI.get(recipeId).then((r) => r.data),
    enabled: Boolean(recipeId),
    ...options,
  })
}

export function useRecipeVersions(recipeId, options = {}) {
  return useQuery({
    queryKey: recipeKeys.versions(recipeId),
    queryFn: () => recipesAPI.listVersions(recipeId).then((r) => r.data),
    enabled: Boolean(recipeId),
    ...options,
  })
}

export function useRecipeCategories(lang = 'en') {
  return useQuery({
    queryKey: recipeKeys.categories(lang),
    queryFn: () =>
      recipesAPI.listCategories({ lang }).then((r) => r.data),
    staleTime: 1000 * 60 * 60, // categories rarely change — 1 hour
  })
}

export function useEquipmentConfig() {
  return useQuery({
    queryKey: recipeKeys.equipment,
    queryFn: () => recipesAPI.getEquipment().then((r) => r.data),
    staleTime: 1000 * 60 * 5,
  })
}

// ---- Write hooks ---------------------------------------------------------

export function useCreateRecipe() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload) => recipesAPI.create(payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recipeKeys.lists() })
    },
  })
}

export function useDeleteRecipe() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id) => recipesAPI.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recipeKeys.lists() })
    },
  })
}

export function useAIGenerateDraft() {
  return useMutation({
    mutationFn: (payload) =>
      recipesAPI.aiGenerate(payload).then((r) => r.data),
  })
}

export function usePhotoDraft() {
  return useMutation({
    mutationFn: (formData) =>
      recipesAPI.fromPhoto(formData).then((r) => r.data),
  })
}

export function useModifyVersion(recipeId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ versionId, payload }) =>
      recipesAPI
        .modifyVersion(recipeId, versionId, payload)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recipeKeys.detail(recipeId) })
      qc.invalidateQueries({ queryKey: recipeKeys.versions(recipeId) })
      qc.invalidateQueries({ queryKey: recipeKeys.lists() })
    },
  })
}

export function useDeleteVersion(recipeId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (versionId) =>
      recipesAPI.deleteVersion(recipeId, versionId).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recipeKeys.detail(recipeId) })
      qc.invalidateQueries({ queryKey: recipeKeys.versions(recipeId) })
      qc.invalidateQueries({ queryKey: recipeKeys.lists() })
    },
  })
}

export function useForkVersion(recipeId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload) =>
      recipesAPI.fork(recipeId, payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recipeKeys.detail(recipeId) })
      qc.invalidateQueries({ queryKey: recipeKeys.versions(recipeId) })
    },
  })
}

export function useSwitchCurrentVersion(recipeId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (versionId) =>
      recipesAPI
        .switchCurrentVersion(recipeId, versionId)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recipeKeys.detail(recipeId) })
      qc.invalidateQueries({ queryKey: recipeKeys.versions(recipeId) })
      qc.invalidateQueries({ queryKey: recipeKeys.lists() })
    },
  })
}

export function useTranslateRecipe(recipeId) {
  return useMutation({
    mutationFn: (targetLanguage) =>
      recipesAPI.translate(recipeId, targetLanguage).then((r) => r.data),
  })
}

// VH-107: AI scaling suggestion. Mutation only — the response is held in
// component state for the user to edit before confirming. No cache
// invalidation here: nothing is written to the DB by /scale-suggest. The
// follow-up Save path (useForkVersion) handles its own invalidations,
// and the Shopping-only path is wired via useAddScaledToShoppingList.
export function useScaleRecipeSuggest(recipeId) {
  return useMutation({
    mutationFn: (target) =>
      recipesAPI.scaleSuggest(recipeId, target).then((r) => r.data),
  })
}

export function useUpdateEquipment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload) =>
      recipesAPI.updateEquipment(payload).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recipeKeys.equipment })
      // Equipment changes affect compatibility flags — refresh the list view.
      qc.invalidateQueries({ queryKey: recipeKeys.lists() })
    },
  })
}

// =====================================================================
// VH-101 / VH-102: Recipe-level media mutations
// =====================================================================

function _invalidateRecipe(qc, recipeId) {
  qc.invalidateQueries({ queryKey: recipeKeys.detail(recipeId) })
  qc.invalidateQueries({ queryKey: recipeKeys.lists() })
}

export function useUploadCoverImage(recipeId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (formData) =>
      recipesAPI.uploadCoverImage(recipeId, formData).then((r) => r.data),
    onSuccess: () => _invalidateRecipe(qc, recipeId),
  })
}

export function useDeleteCoverImage(recipeId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      recipesAPI.deleteCoverImage(recipeId).then((r) => r.data),
    onSuccess: () => _invalidateRecipe(qc, recipeId),
  })
}

export function useSetRecipeVideoYoutube(recipeId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (youtubeUrl) =>
      recipesAPI.setVideoYoutube(recipeId, youtubeUrl).then((r) => r.data),
    onSuccess: () => _invalidateRecipe(qc, recipeId),
  })
}

export function useUploadRecipeVideoFile(recipeId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (formData) =>
      recipesAPI.uploadVideoFile(recipeId, formData).then((r) => r.data),
    onSuccess: () => _invalidateRecipe(qc, recipeId),
  })
}

export function useDeleteRecipeVideo(recipeId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      recipesAPI.deleteVideo(recipeId).then((r) => r.data),
    onSuccess: () => _invalidateRecipe(qc, recipeId),
  })
}
